import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Building2, Globe, MapPin, Users2, Sparkles, Save, Contact as ContactIcon } from 'lucide-react';
import AdminHeader from './AdminHeader';
import { useAdminAuth } from '../context/AdminAuthContext';
import { crmCompany, crmUpdateCompany, crmEnrichCompany } from '../lib/marketingApi';

const STAGE_STYLE = {
  new: 'bg-slate-100 text-slate-600', subscriber: 'bg-sky-100 text-sky-700',
  lead: 'bg-amber-100 text-amber-700', engaged: 'bg-violet-100 text-violet-700',
  customer: 'bg-emerald-100 text-emerald-700', cold: 'bg-rose-100 text-rose-600',
};

export default function CompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAdminAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'editor';
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('contacts');
  const [form, setForm] = useState({});
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [enriching, setEnriching] = useState(false);

  const load = () => crmCompany(id).then((d) => {
    setData(d);
    const c = d.company || {};
    setForm({ segment: c.segment || '', size: c.size || '', industry: c.industry || '', hq: c.hq || '', website: c.website || '', description: c.description || '', owner: c.owner || '' });
  }).catch(() => toast.error('Failed to load company'));
  useEffect(() => { load(); }, [id]);

  const saveProfile = async () => {
    setSaving(true);
    try { await crmUpdateCompany(id, form); toast.success('Company profile saved'); load(); }
    catch (e) { toast.error('Save failed'); } finally { setSaving(false); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try { await crmUpdateCompany(id, { note }); setNote(''); toast.success('Note added'); load(); }
    catch (e) { toast.error('Failed'); } finally { setSaving(false); }
  };
  const enrich = async () => {
    setEnriching(true);
    try { await crmEnrichCompany(id); toast.success('AI enrichment complete'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Enrichment failed'); } finally { setEnriching(false); }
  };

  if (!data) return (<div className="min-h-screen bg-slate-50"><AdminHeader active="companies" /><div className="flex items-center justify-center py-32 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div></div>);

  const c = data.company || {};
  const contacts = data.contacts || [];
  const enr = (c.enrichment && c.enrichment.company) || null;
  const inp = 'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#0035A8]';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="company-detail">
      <AdminHeader active="companies" />
      <div className="mx-auto max-w-6xl px-5 py-6 sm:px-8">
        <button onClick={() => navigate('/admin/companies')} className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800"><ArrowLeft className="h-4 w-4" /> Companies</button>

        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#0035A8]/10 text-lg font-bold text-[#0035A8]"><Building2 className="h-6 w-6" /></span>
            <div className="min-w-0">
              <div className="truncate text-lg font-bold text-slate-900" data-testid="company-name">{c.name || '—'}</div>
              <div className="truncate text-sm text-slate-500">{c.domain || c.key}</div>
            </div>
            <div className="ml-auto flex items-center gap-4 text-sm">
              <div className="text-center"><div className="text-lg font-bold text-slate-900">{c.contact_count || 0}</div><div className="text-xs text-slate-400">Contacts</div></div>
              {c.segment ? <span className="rounded-full bg-[#0035A8]/10 px-2.5 py-1 text-xs font-semibold text-[#0035A8]">{c.segment}</span> : null}
            </div>
          </div>
        </div>

        <div className="mt-4 inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-100 p-1">
          {['contacts', 'profile'].map((tk) => (
            <button key={tk} onClick={() => setTab(tk)} data-testid={`company-tab-${tk}`}
              className={`rounded-md px-4 py-1.5 text-sm font-semibold capitalize transition-all ${tab === tk ? 'bg-white text-[#0035A8] shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>
              {tk === 'profile' ? 'Company Profile' : `Contacts (${contacts.length})`}
            </button>
          ))}
        </div>

        {tab === 'contacts' ? (
          <div className="mt-4 overflow-hidden rounded-lg border border-slate-200 bg-white" data-testid="company-contacts">
            <div className="hidden grid-cols-12 gap-3 border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 md:grid">
              <div className="col-span-4">Name / Email</div><div className="col-span-3">Title</div>
              <div className="col-span-3">Sources</div><div className="col-span-2">Stage</div>
            </div>
            {contacts.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-14 text-slate-400"><ContactIcon className="h-8 w-8" /><p className="text-sm">No contacts.</p></div>
            ) : contacts.map((p) => (
              <button key={p.id} onClick={() => navigate(`/admin/contacts/${p.id}`)} data-testid={`company-contact-${p.email}`}
                className="grid w-full grid-cols-1 gap-2 border-b border-slate-100 px-5 py-3 text-left hover:bg-slate-50 md:grid-cols-12 md:items-center md:gap-3">
                <div className="col-span-4 min-w-0"><div className="truncate text-sm font-semibold text-slate-900">{p.name || p.email}</div><div className="truncate text-xs text-slate-400">{p.email}</div></div>
                <div className="col-span-3 truncate text-sm text-slate-600">{p.job_title || '—'}</div>
                <div className="col-span-3 flex flex-wrap gap-1">{(p.sources || []).slice(0, 3).map((s) => <span key={s} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">{s}</span>)}</div>
                <div className="col-span-2"><span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STAGE_STYLE[p.stage] || 'bg-slate-100 text-slate-600'}`}>{p.stage || 'new'}</span></div>
              </button>
            ))}
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2 rounded-lg border border-slate-200 bg-white p-5" data-testid="company-profile-form">
              <div className="mb-3 text-sm font-semibold text-slate-800">Company profile</div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="text-xs font-medium text-slate-500">Segment
                  <input value={form.segment} onChange={(e) => setForm({ ...form, segment: e.target.value })} disabled={!canEdit} data-testid="company-segment" className={`mt-1 ${inp}`} placeholder="e.g. multifamily" /></label>
                <label className="text-xs font-medium text-slate-500">Size
                  <input value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} disabled={!canEdit} data-testid="company-size" className={`mt-1 ${inp}`} placeholder="e.g. 201-500" /></label>
                <label className="text-xs font-medium text-slate-500">Industry
                  <input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} disabled={!canEdit} data-testid="company-industry" className={`mt-1 ${inp}`} placeholder="e.g. Property Management" /></label>
                <label className="text-xs font-medium text-slate-500">HQ
                  <input value={form.hq} onChange={(e) => setForm({ ...form, hq: e.target.value })} disabled={!canEdit} data-testid="company-hq" className={`mt-1 ${inp}`} placeholder="e.g. Boston, MA" /></label>
                <label className="text-xs font-medium text-slate-500">Assigned to
                  <input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} disabled={!canEdit} data-testid="company-owner" className={`mt-1 ${inp}`} placeholder="Assign owner…" /></label>
                <label className="text-xs font-medium text-slate-500 sm:col-span-2">Website
                  <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} disabled={!canEdit} data-testid="company-website" className={`mt-1 ${inp}`} placeholder="https://…" /></label>
                <label className="text-xs font-medium text-slate-500 sm:col-span-2">Description
                  <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} disabled={!canEdit} data-testid="company-description" rows={4} className={`mt-1 ${inp}`} /></label>
              </div>
              {canEdit && (
                <button onClick={saveProfile} disabled={saving} data-testid="company-save-btn" className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-[#0035A8] px-4 py-2 text-sm font-semibold text-white hover:bg-[#002B87] disabled:opacity-60">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save profile
                </button>
              )}

              <div className="mt-5 border-t border-slate-100 pt-4">
                <div className="mb-2 text-sm font-semibold text-slate-800">Notes</div>
                {canEdit && (
                  <div className="flex gap-2">
                    <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" data-testid="company-note-input" className={inp} />
                    <button onClick={addNote} disabled={saving} data-testid="company-note-save" className="inline-flex items-center gap-1.5 rounded-md bg-[#0035A8] px-3.5 text-sm font-semibold text-white hover:bg-[#002B87] disabled:opacity-60"><Save className="h-4 w-4" /></button>
                  </div>
                )}
                <div className="mt-3 space-y-2">
                  {[...(c.notes_log || [])].reverse().map((n) => (
                    <div key={n.id} className="rounded-md border border-slate-100 px-3 py-2 text-sm text-slate-700">{n.text}<div className="text-xs text-slate-400">{n.by}</div></div>
                  ))}
                  {(c.notes_log || []).length === 0 ? <p className="text-sm text-slate-400">No notes yet.</p> : null}
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-violet-200 bg-violet-50/60 p-5" data-testid="company-ai-research">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-semibold text-violet-800"><Sparkles className="h-4 w-4" /> AI research</div>
                {canEdit && (
                  <button onClick={enrich} disabled={enriching} data-testid="company-enrich-btn" className="inline-flex items-center gap-1.5 rounded-md bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700 disabled:opacity-60">
                    {enriching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} {enr ? 'Re-run' : 'Enrich'}
                  </button>
                )}
              </div>
              {enr ? (
                <div className="mt-3 space-y-2 text-sm text-slate-700">
                  {enr.summary ? <p>{enr.summary}</p> : null}
                  {enr.industry ? <div className="flex items-center gap-2"><Building2 className="h-3.5 w-3.5 text-slate-400" /> {enr.industry}</div> : null}
                  {enr.size ? <div className="flex items-center gap-2"><Users2 className="h-3.5 w-3.5 text-slate-400" /> {enr.size}</div> : null}
                  {enr.hq ? <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-slate-400" /> {enr.hq}</div> : null}
                  {enr.website ? <div className="flex items-center gap-2"><Globe className="h-3.5 w-3.5 text-slate-400" /> {enr.website}</div> : null}
                  {(enr.products || []).length ? <div><div className="text-xs font-semibold text-slate-500">Products</div><div className="mt-1 flex flex-wrap gap-1">{enr.products.map((p, i) => <span key={i} className="rounded-full bg-white px-2 py-0.5 text-xs text-slate-600">{p}</span>)}</div></div> : null}
                  <p className="mt-2 text-[11px] text-slate-400">{c.enrichment?.disclaimer}</p>
                </div>
              ) : <p className="mt-3 text-sm text-slate-500">Run AI research to draft a best-effort company profile from the domain and name. Labelled estimate — verify before use.</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
